/**
 * SmallMultiplesGrid — grid of mini per-well line charts with click-to-expand.
 * Native hand-rolled SVG (no Recharts).
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { scaleTime, scaleLinear } from 'd3-scale';
import { line as d3Line, curveMonotoneX } from 'd3-shape';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { WELL_COLORS } from '../../chartTheme';
import { ChartTooltip } from '../../ChartTooltip';
import { useVisibility } from '../../../hooks/useVisibility';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

interface CellData {
  wellName: string;
  wellIndex: number;
  points: Array<{ ts: number; value: number; date: string }>;
  mcl: number | null;
  latestValue: number | null;
  minVal: number;
  maxVal: number;
  minTs: number;
  maxTs: number;
}

const LAZY_THRESHOLD = 12;
const CELL_HEIGHT = 130;
const CELL_PADDING = 6;

function LazyCell({ children, enabled }: { children: React.ReactNode; enabled: boolean }) {
  const [ref, visible] = useVisibility('200px');
  if (!enabled) return <>{children}</>;
  return (
    <div ref={ref} style={{ minHeight: 144 }}>
      {visible ? children : (
        <div className="animate-pulse rounded-lg border p-2" style={{ borderColor: 'var(--dv-border-subtle)', height: 144 }}>
          <div className="h-2 w-16 bg-mono-800/60 rounded mb-2" />
          <div className="h-[120px] bg-mono-800/30 rounded" />
        </div>
      )}
    </div>
  );
}

export function SmallMultiplesGrid({ data, unit }: Props) {
  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound]) => ({ code, compound }));
  }, [data.wells]);

  const defaultAnalyte = useMemo(() => {
    const benzene = allAnalytes.find((a) => a.compound.toLowerCase() === 'benzene');
    return benzene?.code ?? allAnalytes[0]?.code ?? '';
  }, [allAnalytes]);

  const [selectedAnalyte, setSelectedAnalyte] = useState<string>(defaultAnalyte);
  const [expandedWell, setExpandedWell] = useState<string | null>(null);

  const cells: CellData[] = useMemo(() => {
    const result: CellData[] = [];
    data.wells.forEach((well, wi) => {
      const analyteData = well.analytes[selectedAnalyte];
      if (!analyteData) return;
      const points = analyteData.measurements
        .map((m) => {
          const v = convertConc(m.concentration, unit);
          return v !== null ? { ts: new Date(m.date).getTime(), value: v, date: m.date } : null;
        })
        .filter((p): p is { ts: number; value: number; date: string } => p !== null)
        .sort((a, b) => a.ts - b.ts);
      if (points.length === 0) return;
      const values = points.map((p) => p.value);
      const mcl = convertConc(analyteData.mcl, unit);
      result.push({
        wellName: well.well_name,
        wellIndex: wi,
        points,
        mcl,
        latestValue: values[values.length - 1],
        minVal: Math.min(...values, ...(mcl !== null ? [mcl] : [])),
        maxVal: Math.max(...values, ...(mcl !== null ? [mcl] : [])),
        minTs: points[0].ts,
        maxTs: points[points.length - 1].ts,
      });
    });
    return result;
  }, [data.wells, selectedAnalyte, unit]);

  const handleCellClick = useCallback((wellName: string) => {
    setExpandedWell(wellName);
  }, []);

  const handleCloseOverlay = useCallback(() => {
    setExpandedWell(null);
  }, []);

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  const expandedCell = expandedWell ? cells.find((c) => c.wellName === expandedWell) : null;

  return (
    <>
      <div role="img" aria-label="Small multiples grid of per-well time series">
        <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
          Small multiples grid showing mini time-series charts — one per well — for side-by-side
          comparison. Click any chart to expand it to full size.
        </p>

        <div className="flex items-center gap-3 mb-4">
          <label className="text-xs font-medium" style={{ color: 'var(--dv-text-muted)' }}>
            Analyte
          </label>
          <select
            value={selectedAnalyte}
            onChange={(e) => setSelectedAnalyte(e.target.value)}
            className="text-xs rounded px-2 py-1 border"
            style={{
              background: 'var(--dv-bg-card)',
              borderColor: 'var(--dv-border-subtle)',
              color: 'var(--dv-text-primary)',
            }}
          >
            {allAnalytes.map((a) => (
              <option key={a.code} value={a.code}>{a.compound}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {cells.map((cell) => (
            <LazyCell key={cell.wellName} enabled={cells.length > LAZY_THRESHOLD}>
              <MiniCell cell={cell} onClick={() => handleCellClick(cell.wellName)} />
            </LazyCell>
          ))}
        </div>
      </div>

      {expandedCell && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.6)' }}
          onClick={handleCloseOverlay}
        >
          <div
            className="relative rounded-xl border p-6 w-full max-w-3xl mx-4"
            style={{
              background: 'var(--dv-bg-card)',
              borderColor: 'var(--dv-border-subtle)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseOverlay}
              className="absolute top-3 right-3 p-1.5 rounded-lg border transition-colors hover:bg-mono-800"
              style={{
                borderColor: 'var(--dv-border-subtle)',
                color: 'var(--dv-text-muted)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 1l12 12M13 1L1 13" />
              </svg>
            </button>

            <div className="mb-4">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--dv-text-primary)' }}>
                {expandedCell.wellName}
              </h3>
              <span className="text-[11px]" style={{ color: 'var(--dv-text-muted)' }}>
                {allAnalytes.find((a) => a.code === selectedAnalyte)?.compound ?? selectedAnalyte}
                {' '}&middot;{' '}
                {expandedCell.points.length} measurements
              </span>
            </div>

            <ExpandedCell cell={expandedCell} unit={unit} />
          </div>
        </div>
      )}
    </>
  );
}

function MiniCell({ cell, onClick }: { cell: CellData; onClick: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(160);
  const color = WELL_COLORS[cell.wellIndex % WELL_COLORS.length];

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - CELL_PADDING * 2);
  const innerHeight = CELL_HEIGHT - CELL_PADDING * 2;

  const { xScale, yScale, pathD } = useMemo(() => {
    if (cell.points.length === 0) {
      return {
        xScale: scaleTime().domain([0, 1]).range([CELL_PADDING, CELL_PADDING + innerWidth]),
        yScale: scaleLinear().domain([0, 1]).range([CELL_PADDING + innerHeight, CELL_PADDING]),
        pathD: '',
      };
    }
    const x = scaleTime()
      .domain([cell.minTs === cell.maxTs ? cell.minTs - 1 : cell.minTs, cell.minTs === cell.maxTs ? cell.maxTs + 1 : cell.maxTs])
      .range([CELL_PADDING, CELL_PADDING + innerWidth]);
    const y = scaleLinear()
      .domain([cell.minVal, cell.maxVal === cell.minVal ? cell.maxVal + 1 : cell.maxVal])
      .range([CELL_PADDING + innerHeight, CELL_PADDING]);
    const gen = d3Line<{ ts: number; value: number; date: string }>()
      .x((d) => x(d.ts))
      .y((d) => y(d.value))
      .curve(curveMonotoneX);
    return { xScale: x, yScale: y, pathD: gen(cell.points) ?? '' };
  }, [cell, innerWidth, innerHeight]);

  void xScale;

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      className="rounded-lg border p-2 cursor-pointer transition-colors"
      style={{ borderColor: 'var(--dv-border-subtle)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--dv-accent-cyan-border)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--dv-border-subtle)';
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium truncate" style={{ color: 'var(--dv-text-primary)' }}>
          {cell.wellName}
        </span>
        {cell.latestValue !== null && (
          <span className="text-[10px] font-mono ml-1 shrink-0" style={{ color }}>
            {cell.latestValue.toFixed(3)}
          </span>
        )}
      </div>
      <svg width={width || '100%'} height={CELL_HEIGHT} style={{ display: 'block' }}>
        {cell.mcl !== null && (() => {
          const y = yScale(cell.mcl);
          if (y < CELL_PADDING || y > CELL_PADDING + innerHeight) return null;
          return (
            <line
              x1={CELL_PADDING}
              x2={CELL_PADDING + innerWidth}
              y1={y}
              y2={y}
              stroke="#ef4444"
              strokeWidth={1}
              strokeDasharray="4 3"
              opacity={0.7}
            />
          );
        })()}
        {pathD && (
          <path d={pathD} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
        )}
      </svg>
    </div>
  );
}

function ExpandedCell({ cell, unit }: { cell: CellData; unit: ConcentrationUnit }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(720);
  const HEIGHT = 360;
  const MARGIN = { top: 16, right: 24, bottom: 36, left: 56 };
  const color = WELL_COLORS[cell.wellIndex % WELL_COLORS.length];

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, HEIGHT - MARGIN.top - MARGIN.bottom);

  const yMax = Math.max(cell.maxVal, cell.mcl ?? 0) * 1.1;
  const xScale = useMemo(
    () => scaleTime().domain([cell.minTs, cell.maxTs]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [cell.minTs, cell.maxTs, innerWidth],
  );
  const yScale = useMemo(
    () => scaleLinear().domain([0, yMax || 1]).range([MARGIN.top + innerHeight, MARGIN.top]).nice(),
    [yMax, innerHeight],
  );

  const pathD = useMemo(() => {
    const gen = d3Line<{ ts: number; value: number; date: string }>()
      .x((d) => xScale(d.ts))
      .y((d) => yScale(d.value))
      .curve(curveMonotoneX);
    return gen(cell.points) ?? '';
  }, [cell.points, xScale, yScale]);

  const xTicks = useMemo(() => xScale.ticks(6), [xScale]);
  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const formatX = (d: Date) => `${d.getMonth() + 1}/${d.getFullYear().toString().slice(2)}`;
  const formatY = (v: number) => (v < 1 ? v.toPrecision(2) : v.toFixed(1));

  interface HoverState { point: { ts: number; value: number; date: string }; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const lx = e.clientX - rect.left;
      let best: { ts: number; value: number; date: string } | null = null;
      let bestDx = Infinity;
      for (const p of cell.points) {
        const px = xScale(p.ts);
        const dx = Math.abs(px - lx);
        if (dx < bestDx) {
          bestDx = dx;
          best = p;
        }
      }
      if (best && bestDx < 30) setHover({ point: best, clientX: e.clientX, clientY: e.clientY });
      else setHover(null);
    },
    [cell.points, xScale],
  );
  const handleLeave = useCallback(() => setHover(null), []);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg ref={svgRef} width={width || '100%'} height={HEIGHT} style={{ display: 'block', cursor: 'crosshair' }}>
        {yTicks.map((t) => {
          const y = yScale(t);
          return (
            <line
              key={`g-${t}`}
              x1={MARGIN.left}
              x2={MARGIN.left + innerWidth}
              y1={y}
              y2={y}
              stroke="var(--dv-chart-grid)"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          );
        })}

        {cell.mcl !== null && (() => {
          const y = yScale(cell.mcl);
          if (y < MARGIN.top || y > MARGIN.top + innerHeight) return null;
          return (
            <g style={{ filter: 'url(#dv-mcl-glow)' }}>
              <line
                x1={MARGIN.left}
                x2={MARGIN.left + innerWidth}
                y1={y}
                y2={y}
                stroke="#ef4444"
                strokeWidth={1.5}
                strokeDasharray="6 4"
              />
              <text x={MARGIN.left + innerWidth - 4} y={y - 4} textAnchor="end" fontSize={9} fill="#ef4444" fontWeight={600}>
                Reg Limit
              </text>
            </g>
          );
        })()}

        <path d={pathD} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
        {cell.points.map((p, i) => {
          const isHovered = hover?.point.ts === p.ts;
          return (
            <circle
              key={`d-${i}`}
              cx={xScale(p.ts)}
              cy={yScale(p.value)}
              r={isHovered ? 4.5 : 3}
              fill={color}
              stroke="var(--dv-bg-card)"
              strokeWidth={isHovered ? 2 : 1}
              style={isHovered ? { filter: 'url(#dv-hover-halo)' } : undefined}
            />
          );
        })}

        <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
        {yTicks.map((t) => {
          const y = yScale(t);
          return (
            <g key={`yt-${t}`}>
              <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
              <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatY(t)}
              </text>
            </g>
          );
        })}
        <text transform={`translate(${MARGIN.left - 44}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
          {`Conc (${unit})`}
        </text>

        <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
        {xTicks.map((t, i) => {
          const x = xScale(t);
          return (
            <g key={`xt-${i}`}>
              <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
              <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatX(t)}
              </text>
            </g>
          );
        })}

        <rect
          x={MARGIN.left}
          y={MARGIN.top}
          width={innerWidth}
          height={innerHeight}
          fill="transparent"
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
        />
      </svg>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 160, fontSize: 11 }}>
            <div style={{ fontSize: 10, color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              {hover.point.date}
            </div>
            <div style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)', fontWeight: 600 }}>
              {hover.point.value.toFixed(4)} {unit}
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
